import { useState, type FormEvent } from 'react'
import { Link, useSearchParams } from 'react-router-dom'

import { Banner, Card, Skeleton } from '../../components/ui.tsx'
import { ApiError, post } from '../../lib/api.ts'
import { useApi } from '../../lib/api.ts'
import { AuthCard, AuthHeading, PasswordStrength } from './AuthCard.tsx'
import { assessPassword } from '../../../shared/auth.ts'

/**
 * Choose a new password from a reset link.
 *
 * Setting it signs out every other session — whoever knew the old password does not get
 * to stay signed in through a reset.
 */
export function ResetPassword() {
  const [params] = useSearchParams()
  const token = params.get('token') ?? ''

  const { data, error, loading } = useApi<{ email: string }>(token ? `/auth/reset/${token}` : null)
  const [password, setPassword] = useState('')
  const [confirm, setConfirm] = useState('')
  const [busy, setBusy] = useState(false)
  const [done, setDone] = useState(false)
  const [err, setErr] = useState<string | null>(null)

  const verdict = assessPassword(password, data?.email ?? '')
  const mismatch = confirm.length > 0 && confirm !== password

  const submit = async (e: FormEvent) => {
    e.preventDefault()
    setBusy(true)
    setErr(null)
    try {
      await post('/auth/reset', { token, password })
      setDone(true)
    } catch (error) {
      setErr(error instanceof ApiError ? error.message : String(error))
    } finally {
      setBusy(false)
    }
  }

  if (done) {
    return (
      <AuthCard width={420}>
        <AuthHeading title="Password updated" />
        <Card pad>
          <Banner tone="success" icon="✓" title="Your password has been changed" flush>
            Every other session has been signed out. Use your new password to sign in.
          </Banner>
          <Link className="btn primary block lg" to="/login" style={{ marginTop: 14 }}>
            Go to log in
          </Link>
        </Card>
      </AuthCard>
    )
  }

  // An expired or already-used link is a normal outcome of a flow that plays out over
  // time, so it gets a real explanation and a way forward.
  if (!token || error) {
    return (
      <AuthCard width={420}>
        <AuthHeading title="Reset your password" />
        <Card pad>
          <Banner tone="halt" icon="⚠" title="This link has expired" flush>
            {error ??
              'Password reset links are only valid for 60 minutes and can only be used once. Request a new one.'}
          </Banner>
          <Link className="btn outline block sm" to="/recover" style={{ marginTop: 14 }}>
            Request a new link
          </Link>
        </Card>
      </AuthCard>
    )
  }

  return (
    <AuthCard width={420}>
      <AuthHeading title="Choose a new password" />
      <Card pad>
        {loading || !data ? (
          <Skeleton rows={3} />
        ) : (
          <form onSubmit={submit} noValidate>
            <div className="cell-sub" style={{ marginBottom: 14 }}>
              For <b>{data.email}</b>
            </div>

            <div className="field">
              <label htmlFor="rc-new">New password</label>
              <input
                className="input"
                type="password"
                id="rc-new"
                placeholder="At least 10 characters"
                autoComplete="new-password"
                autoFocus
                required
                value={password}
                onChange={(e) => setPassword(e.target.value)}
              />
              <PasswordStrength password={password} email={data.email} />
            </div>

            <div className="field" style={{ marginBottom: 0 }}>
              <label htmlFor="rc-confirm">Confirm new password</label>
              <input
                className="input"
                type="password"
                id="rc-confirm"
                autoComplete="new-password"
                required
                value={confirm}
                onChange={(e) => setConfirm(e.target.value)}
                style={mismatch ? { borderColor: 'var(--neg)' } : undefined}
              />
              {mismatch && (
                <span className="help warn" role="alert">
                  The two passwords don't match.
                </span>
              )}
              {err && (
                <span className="help warn" role="alert">
                  {err}
                </span>
              )}
            </div>

            <button
              type="submit"
              className="btn primary block lg"
              style={{ marginTop: 16 }}
              disabled={busy || !verdict.acceptable || mismatch || !confirm}
            >
              {busy ? 'Saving…' : 'Set new password'}
            </button>
          </form>
        )}
      </Card>
    </AuthCard>
  )
}
